import { useAxios } from '../../../contexts/AxiosContext'
import { User, UserInvitation, UserProfile, Workspace } from '../../../types'

export interface LoginResponse {
  access_token: string
  user: User
  workspaces: Array<Workspace>
  active_workspace: Array<Workspace>
}

export interface RegisterResponse {
  message: string
}

export interface VerifyOtpResponse extends LoginResponse { }

export interface ResendOtpResponse {
  message: string
  email: string
}

export interface ForgotPasswordResponse {
  message: string
  email: string
}

export interface ResetPasswordResponse {
  message: string
}

export interface LogoutResponse {
  message: string
}

export interface UpdateProfileRequest {
  name?: string | null
  password?: string | null
}

export const useAuthService = () => {
  const { axiosApiInstance } = useAxios()

  const login = async (email: string, password: string): Promise<LoginResponse> => {
    const response = await axiosApiInstance.post<LoginResponse>('/login', { email, password })
    return response.data
  }

  const register = async (email: string, password: string, name: string): Promise<RegisterResponse> => {
    const response = await axiosApiInstance.post<RegisterResponse>('/register', { email, password, name })
    return response.data
  }

  const verifyOtp = async (token: string, email: string): Promise<VerifyOtpResponse> => {
    const response = await axiosApiInstance.post<VerifyOtpResponse>('/verify/otp', { token, email })
    return response.data
  }

  const resendOtp = async (email: string): Promise<ResendOtpResponse> => {
    const response = await axiosApiInstance.post<ResendOtpResponse>('/resend/otp', { email })
    return response.data
  }

  const forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
    const response = await axiosApiInstance.post<ForgotPasswordResponse>('/forgot-password', { email })
    return response.data
  }

  const resetPassword = async (token: string, email: string, newPassword: string): Promise<ResetPasswordResponse> => {
    const response = await axiosApiInstance.post<ResetPasswordResponse>('/reset-password', { token, email, newPassword })
    return response.data
  }

  const getProfile = async (): Promise<UserProfile> => {
    const response = await axiosApiInstance.get<UserProfile>('/profile')
    return response.data
  }

  const updateProfile = async ({ name, password }: UpdateProfileRequest): Promise<User> => {
    const response = await axiosApiInstance.put<User>('/profile', { name, password })
    return response.data
  }

  const getInvites = async (): Promise<Array<UserInvitation>> => {
    const response = await axiosApiInstance.get<Array<UserInvitation>>('/get-invites')
    return response.data
  }

  const logout = async (): Promise<LogoutResponse> => {
    const response = await axiosApiInstance.post<LogoutResponse>('/logout')
    return response.data
  }

  return {
    login,
    register,
    verifyOtp,
    resendOtp,
    forgotPassword,
    resetPassword,
    getProfile,
    updateProfile,
    getInvites,
    logout
  }
}
